import React, { useEffect, useRef, useState, useTransition } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { blue, green, red2, yellow } from 'app/components/ThemeProvider/colors';
import { useFilterChangeTransactions } from 'app/utils/services/transactionsService';
import { AccountValue, LastOperationsType } from 'app/constants/types';
import { useTranslation } from 'react-i18next';
import ExpenseIcon from 'app/components/Icons/ExpenseIcon';
import IncomingIcon from 'app/components/Icons/IncomingIcon';
import { getTheme } from '../ThemeProvider/theme';
import { useLocation, useNavigate } from 'react-router-dom';

interface PaymentStatus {
  value: string;
  label: string;
  icon?: JSX.Element;
  status?: any;
}

interface Props {
  selectedAccount?: AccountValue;
  homePage?: boolean;
  tableData?: LastOperationsType[];
}

const useLastOperations = (props: Props) => {
  const theme = getTheme('light');
  const largeScreens = useMediaQuery(theme.breakpoints.down('lg'));
  const mediumScreens = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedAccount, homePage, tableData } = props;
  const { t } = useTranslation();
  const [_, startTransition] = useTransition();
  const [searchInput, setSearchInput] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [selectValue, setSelectValue] = useState<PaymentStatus[]>([]);
  const [btnId, setBtnId] = useState(1);

  const [accNo, setAccNo] = useState<any>('');
  const [paymentType, setPaymentType] = useState<any>(null);
  const [client, setClient] = useState('');
  const [voen, setVoen] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState<{ value: string; label: string }>(null);
  const [resNextDate, setNextResDate] = useState('');
  const [resEndDate, setEndResDate] = useState('');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [resDate, setResDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [isClean, setIsClean] = useState(false);

  const [filterCounter, setFilterCounter] = useState(0);
  const [mainCount, setMainCount] = useState(0);
  const [showCount, setShowCount] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [tableRow, setTableRow] = useState<LastOperationsType[]>(tableData);
  const [loader, setLoader] = useState(false);

  const [page, setPage] = useState(1);

  const filterBtns = [
    { id: 1, title: t('homePage.all') },
    { id: 2, title: t('homePage.outflw'), icon: <IncomingIcon /> },
    { id: 3, title: t('homePage.inflow'), icon: <ExpenseIcon /> },
  ];

  const paymentStatus = [
    {
      value: 'closed',
      label: t('homePage.confirmed'),
      icon: <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: green }}></Box>,
    },
    {
      value: 'first',
      label: t('homePage.waitingFirst'),
      icon: <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: yellow }}></Box>,
    },
    {
      value: 'second',
      label: t('homePage.waitingSecond'),
      icon: <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: yellow }}></Box>,
    },
    {
      value: 'inprocess',
      label: t('homePage.waitingBank'),
      icon: <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: blue }}></Box>,
    },
    {
      value: 'declined',
      label: t('homePage.reject'),
      icon: <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: red2 }}></Box>,
    },
  ];

  const {
    isLoadingFilterTransactions,
    isSuccessFilterTransactions,
    filterTransactionMutation,
    dataFilterTransactions,
  } = useFilterChangeTransactions();

  useEffect(() => {
    if (homePage) {
      setTableRow(tableData);
    } else if (isSuccessFilterTransactions) {
      setLoader(false);
      setTableRow(dataFilterTransactions?.transactions);
    }
  }, [dataFilterTransactions, homePage]);

  useEffect(() => {
    if (selectedPeriod !== null && selectedPeriod.value !== '-1') {
      setResDate('');
      setNextResDate('');
      setEndResDate('');
      setResDate(null);
      setEndDate(null);

      if (resNextDate.length > 0) {
        setFilterCounter(filterCounter - 1);
      }

      if (resEndDate.length > 0) {
        setFilterCounter(filterCounter - 1);
      }

      if (resNextDate.length > 0 && resEndDate.length > 0) {
        setFilterCounter(filterCounter - 2);
      }
    }
  }, [selectedPeriod]);

  const wrapperRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event: any) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) && searchInputValue.length === 0) {
        setSearchInput(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [wrapperRef, searchInputValue]);

  useEffect(() => {
    if (searchInput && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchInput]);

  const navigate = useNavigate();
  const location = useLocation();
  const lang = location.pathname.substring(0, 3);
  const handleMoveToTransfer = () => {
    navigate(`${lang}/transfer-page`);
  };

  const valueRef = useRef(selectValue);
  valueRef.current = selectValue;

  const selectAllOption = {
    value: 'all',
    label: t('homePage.all'),
  };

  const isOptionSelected = (option: any) => valueRef?.current.some(({ value }: any) => value === option.value);
  // || isSelectAllSelected();

  const getOptions = () => [selectAllOption, ...paymentStatus];
  //
  const getValue = () =>
    // isSelectAllSelected() ? [selectAllOption] :
    selectValue;

  const onChange = (newValue: any, actionMeta: any) => {
    const { action, option, removedValue } = actionMeta;

    if (action === 'select-option' && option.value === selectAllOption.value) {
      setSelectValue(paymentStatus);
    } else if (
      (action === 'deselect-option' && option.value === selectAllOption.value) ||
      (action === 'remove-value' && removedValue.value === selectAllOption.value)
    ) {
      setSelectValue([]);
    } else if (actionMeta.action === 'deselect-option') {
      // setSelectValue(paymentStatus.filter(({ value }: any) => value !== option.value));
      setSelectValue(newValue || []);
    } else {
      setSelectValue(newValue || []);
      // setSelectedValueStatus(newValue.map((item: any) => item.value));
    }

    filterTransactionMutation({
      acc_no: accNo ? [accNo.value] : '',
      end_date: resEndDate,
      start_date: resNextDate,
      period_type: selectedPeriod === null ? 0 : +selectedPeriod.value,
      start_amount: fromAmount,
      end_amount: toAmount,
      cr_custname: client,
      cr_taxno: voen,
      search: searchInputValue,
      db_cr_filter: btnId === 1 ? '' : btnId === 2 ? 'cr' : btnId === 3 && 'db',
      trn_type: paymentType === undefined ? '' : paymentType?.value,
      state: newValue.map((item: any) => item.value),
    });
  };

  const handleSearchInputValue = (event: any) => {
    setSearchInputValue(event.target.value);
  };

  const handleCheckData = (id: number) => {
    const data = {
      acc_no: accNo ? accNo.value.split() : '',
      // end_date: resEndDate,
      // start_date: resNextDate,
      date: resNextDate || resEndDate,
      amount: fromAmount || toAmount,
      cr_custname: client,
      cr_taxno: voen,
      trn_type: paymentType === undefined ? '' : paymentType?.value,
    };
    const nonEmptyFieldsCount = Object.values(data).filter((value) => {
      return value !== '' && value !== undefined && value !== null && value.length !== 0;
    }).length;

    setFilterCounter(nonEmptyFieldsCount);

    setMainCount(nonEmptyFieldsCount);

    filterTransactionMutation({
      acc_no: accNo ? accNo.value.split() : '',
      trn_type: paymentType === undefined ? '' : paymentType?.value,
      end_date: resEndDate,
      start_date: resNextDate,
      period_type: selectedPeriod === null ? 0 : +selectedPeriod.value, // for periods
      start_amount: fromAmount,
      end_amount: toAmount,
      cr_custname: client,
      cr_taxno: voen,
      search: searchInputValue,
      db_cr_filter: id === 1 ? '' : id === 2 ? 'cr' : id === 3 && 'db',
      state: selectValue.map((item: any) => item.value),
    });
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);

    filterTransactionMutation({
      acc_no: accNo ? accNo.value.split() : '',
      trn_type: paymentType === undefined ? '' : paymentType?.value,
      end_date: resEndDate,
      start_date: resNextDate,
      period_type: selectedPeriod === null ? 0 : +selectedPeriod.value,
      start_amount: fromAmount,
      end_amount: toAmount,
      cr_custname: client,
      cr_taxno: voen,
      search: searchInputValue,
      db_cr_filter: btnId === 1 ? '' : btnId === 2 ? 'cr' : btnId === 3 && 'db',
      state: selectValue.map((item: any) => item.value),
      limit: '30',
      offset: String((value - 1) * 30),
    });
  };
  const handleSearch = () => {
    handleCheckData(btnId);
  };

  const handleDeleteSearch = () => {
    if (searchInputValue.length > 0) {
      setSearchInputValue('');
      handleCheckData(btnId);
    } else {
      setSearchInputValue('');
    }
  };

  const handleChangeTransactionType = (id: number) => {
    setPage(1);
    startTransition(() => {
      setBtnId(id);
    });
    handleCheckData(id);
  };

  const handleSubmitFilter = () => {
    setPage(1);
    if (filterCounter > 0) {
      setShowCount(true);
    } else {
      setShowCount(false);
    }

    handleCheckData(btnId);

    // setMainCount(filterCounter);
    // setShowFilter(false);
  };

  const resetAllInput = () => {
    setPaymentType(null);
    setClient('');
    setVoen('');
    setSelectedPeriod(null);
    setNextResDate('');
    setEndResDate('');
    setResDate(null);
    setEndDate(null);
    setFromAmount('');
    setToAmount('');
    setAccNo({ value: '' });
    setIsClean(false);
    setFilterCounter(0);
    setShowCount(false);
    handleCheckData(btnId);
  };

  useEffect(() => {
    if (!homePage) {
      filterTransactionMutation({
        acc_no: selectedAccount === undefined ? '' : [selectedAccount?.value],
        end_date: resEndDate,
        start_date: resNextDate,
        period_type: selectedPeriod === null ? 0 : +selectedPeriod.value,
        start_amount: fromAmount,
        end_amount: toAmount,
        cr_custname: client,
        cr_taxno: voen,
        search: searchInputValue,
        db_cr_filter: btnId === 1 ? '' : btnId === 2 ? 'cr' : btnId === 3 && 'db',
        state: selectValue.map((item: any) => item.value),
        limit: '30',
        offset: '0',
      });
    }
  }, []);

  return {
    t,
    searchInput,
    filterBtns,
    wrapperRef,
    searchRef,
    dataFilterTransactions,
    btnId,
    setBtnId,
    searchInputValue,
    handleDeleteSearch,
    handleSearch,
    setSearchInputValue,
    filterTransactionMutation,
    isOptionSelected,
    handleSearchInputValue,
    setSearchInput,
    getOptions,
    getValue,
    onChange,
    handleChangeTransactionType,
    paymentType,
    setPaymentType,
    voen,
    setVoen,
    client,
    setClient,
    selectedPeriod,
    setSelectedPeriod,
    resNextDate,
    setNextResDate,
    resEndDate,
    setEndResDate,
    fromAmount,
    setFromAmount,
    toAmount,
    setToAmount,
    filterCounter,
    setFilterCounter,
    showCount,
    setShowCount,
    mainCount,
    setMainCount,
    showFilter,
    setShowFilter,
    handleSubmitFilter,
    isClean,
    setIsClean,
    resDate,
    setResDate,
    endDate,
    setEndDate,
    resetAllInput,
    isSuccessFilterTransactions,
    tableRow,
    loader,
    largeScreens,
    mediumScreens,
    paymentStatus,
    handleMoveToTransfer,
    isLoadingFilterTransactions,
    page,
    setPage,
    handleChangePage,
    handleCheckData,
    accNo,
    setAccNo,
  };
};

export default useLastOperations;
